import React, {useMemo} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core';
import {Info} from '@material-ui/icons';
import {makeStyles} from '@material-ui/core/styles';
import usePersistentStore from '../../../../store/persistentStore';
import {
  DEFAULT_STACKED_EXPORT_OPTIONS,
} from '../helpers/exportStackedReportXls';

const EXPORT_TOOLTIPS = {
  sourcesScope:
    'All sources includes every source in the report, even the ones disabled in the table. Selected sources only includes the sources currently enabled.',
  metricsScope:
    'All metrics includes every metric column in the table (impressions, revenue, eCPM, etc.). Selected metric only includes the metric currently active in the graph.',
  fileType:
    'Excel includes the Daily, Summary, and Metadata sheets. CSV includes the daily rows only.',
  includeTotals:
    'Adds a total row for each day in the Daily sheet, and an overall total row in the Summary sheet.',
  includePercentOfTotal:
    'Adds a share column (e.g. “% of Revenue”) next to each metric that can be summed, showing each source as a percentage of the total.',
  includeComparison:
    'Adds daily and summary sheets for the comparison period shown in the graph.\n\nUnavailable when “Show data comparison” is off in graph settings, the period has no comparison data, or the file type is CSV (Excel only).',
};

const useStyles = makeStyles(theme => ({
  selectRow: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  selectField: {
    flex: 1,
    minWidth: 0,
  },
  infoButton: {
    padding: 6,
    marginBottom: 2,
    flexShrink: 0,
  },
  checkboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
  },
}));

const getSelectedMetricLabel = (metricsConfig, enabledMetric) => {
  if (!metricsConfig || !enabledMetric) return null;
  const metrics = Object.values(metricsConfig.perSource || {});
  const match = metrics.find(metric => enabledMetric.includes(metric.dataField));
  return match?.displayName || enabledMetric;
};

const InfoTip = ({title, className}) => (
  <Tooltip title={<span style={{whiteSpace: 'pre-line'}}>{title}</span>} arrow>
    <IconButton
      size="small"
      className={className}
      tabIndex={-1}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Info fontSize="small" />
    </IconButton>
  </Tooltip>
);

const ExportSelectField = ({classes, children, tooltip, style}) => (
  <Box className={classes.selectRow} style={style}>
    <Box className={classes.selectField}>{children}</Box>
    <InfoTip title={tooltip} className={classes.infoButton} />
  </Box>
);

const ExportCheckboxField = ({classes, tooltip, label, ...formControlLabelProps}) => (
  <FormControlLabel
    {...formControlLabelProps}
    style={{marginLeft: 0, marginRight: 0, display: 'flex'}}
    label={
      <span className={classes.checkboxLabel}>
        {label}
        <InfoTip title={tooltip} className={classes.infoButton} />
      </span>
    }
  />
);

const StackedExportDialog = ({open, onClose, onExport, exportPayload}) => {
  const classes = useStyles();
  const options =
    usePersistentStore(state => state.stackedExportOptions) ?? DEFAULT_STACKED_EXPORT_OPTIONS;
  const setPersistentState = usePersistentStore(state => state.setState);

  const setOption = (key, value) => {
    setPersistentState('stackedExportOptions', {...options, [key]: value});
  };

  const selectedMetricLabel = useMemo(
    () => getSelectedMetricLabel(exportPayload?.metricsConfig, exportPayload?.enabledMetric),
    [exportPayload]
  );

  const selectedSourceCount = useMemo(() => {
    const sources = exportPayload?.availableSourcesSorted || [];
    const disabled = exportPayload?.disabledSources || [];
    return sources.filter(source => !disabled.includes(source)).length;
  }, [exportPayload]);

  const handleExport = () => {
    onExport(options);
    onClose();
  };

  const comparisonDisabled = !exportPayload?.comparisonAvailable;
  const isCsv = options.fileType === 'csv';
  const fieldSpacing = {marginTop: 16};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export options</DialogTitle>
      <DialogContent>
        <ExportSelectField classes={classes} tooltip={EXPORT_TOOLTIPS.sourcesScope}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="export-sources-label">Sources</InputLabel>
            <Select
              labelId="export-sources-label"
              value={options.sourcesScope}
              label="Sources"
              onChange={event => setOption('sourcesScope', event.target.value)}
            >
              <MenuItem value="all">All sources</MenuItem>
              <MenuItem value="selected">
                Selected sources only{selectedSourceCount ? ` (${selectedSourceCount})` : ''}
              </MenuItem>
            </Select>
          </FormControl>
        </ExportSelectField>

        <ExportSelectField classes={classes} tooltip={EXPORT_TOOLTIPS.metricsScope} style={fieldSpacing}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="export-metrics-label">Metrics</InputLabel>
            <Select
              labelId="export-metrics-label"
              value={options.metricsScope}
              label="Metrics"
              onChange={event => setOption('metricsScope', event.target.value)}
            >
              <MenuItem value="all">All metrics</MenuItem>
              <MenuItem value="selected">
                Selected metric only{selectedMetricLabel ? ` (${selectedMetricLabel})` : ''}
              </MenuItem>
            </Select>
          </FormControl>
        </ExportSelectField>

        <ExportSelectField classes={classes} tooltip={EXPORT_TOOLTIPS.fileType} style={fieldSpacing}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel id="export-file-label">File type</InputLabel>
            <Select
              labelId="export-file-label"
              value={options.fileType}
              label="File type"
              onChange={event => setOption('fileType', event.target.value)}
            >
              <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
              <MenuItem value="csv">CSV (.csv)</MenuItem>
            </Select>
          </FormControl>
        </ExportSelectField>

        <Divider style={{margin: '16px 0'}} />

        <ExportCheckboxField
          classes={classes}
          tooltip={EXPORT_TOOLTIPS.includeTotals}
          control={
            <Checkbox
              checked={options.includeTotals}
              color="primary"
              onChange={event => setOption('includeTotals', event.target.checked)}
            />
          }
          label="Include totals"
        />

        <ExportCheckboxField
          classes={classes}
          tooltip={EXPORT_TOOLTIPS.includePercentOfTotal}
          control={
            <Checkbox
              checked={options.includePercentOfTotal}
              color="primary"
              onChange={event => setOption('includePercentOfTotal', event.target.checked)}
            />
          }
          label="Include % of total"
        />

        <ExportCheckboxField
          classes={classes}
          tooltip={EXPORT_TOOLTIPS.includeComparison}
          control={
            <Checkbox
              checked={options.includeComparison}
              color="primary"
              disabled={comparisonDisabled || isCsv}
              onChange={event => setOption('includeComparison', event.target.checked)}
            />
          }
          label="Include comparison"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={handleExport}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StackedExportDialog;
